/**
 * 需給ギャップ「供給」系列の表示モデル。
 *
 * 供給の見せ方 (集約 1 本 / 受渡区分カテゴリの積上げ) と、その 1 本ぶんの
 * **系列記述子** (dataKey / 色 / ラベル) をここに閉じる。凡例 (`index.tsx`)・
 * Bar と tooltip (`components/GapChart.tsx`) は同じ配列を読むので、
 * 「凡例が bar を偽って説明する」ズレが構造的に起きない。
 *
 * `models/types.ts` から切り出した (フィルタ型 + proto 変換 + CSV + 日付ヘルパの
 * catch-all に供給カテゴリの表示メタが同居していた)。粒度は隣の
 * `diffChartAxis.ts` / `persistedFilters.ts` と同じ。
 */
import type { GapTradeComponent } from '@ebalance/proto/bffuser/v1/supply_demand_gap_model_pb'
import type { TFunction } from 'i18next'

import {
  GAP_CATEGORY_COLOR_MAP,
  GAP_FALLBACK_COLOR,
} from '~/constants/chartPalette'

/** フィルタ: 供給内訳の表示モード (AC-SDG-004b / AC-SDG-004c)。 既定は 'aggregate'。 */
export const BREAKDOWN_MODES = ['detail', 'aggregate'] as const
export type BreakdownMode = (typeof BREAKDOWN_MODES)[number]

/** localStorage 等の外部由来値を BreakdownMode に絞り込む */
export function isBreakdownMode(v: unknown): v is BreakdownMode {
  return (BREAKDOWN_MODES as readonly string[]).includes(v as string)
}

/** 供給内訳の 1 カテゴリ (backend の `category_code` / `category_name`)。 */
export type CategoryInfo = {
  code: string
  name: string
}

/** 受渡区分が未定義の leg が落ちる番兵カテゴリ (backend `gapCategoryCodeUnknownDeliveryType`)。 */
export const UNKNOWN_SUPPLY_CATEGORY_CODE = 'UNKNOWN_DELIVERY_TYPE'

/**
 * 常時BU カテゴリ (backend `gapCategoryCodeBackupRegular`)。 受渡区分ではなく約定経路
 * (ExecutionRoute = 常時BU) で分類されるため、 受渡区分上は BG 渡しでも相対BG渡しとは
 * 別系列で返る (spec `supply-demand-gap.md` Discussion「常時BU カテゴリの位置付け」=
 * 決定 B「6 カテゴリの独立成分」)。
 */
export const BACKUP_REGULAR_SUPPLY_CATEGORY_CODE = 'BACKUP_REGULAR'

/**
 * ベースロード市場 / 先渡し市場カテゴリ (backend `gapCategoryCodeBaseloadMarket` /
 * `gapCategoryCodeForwardMarket`)。 受渡区分は JEPX だが約定経路がこの 2 値の leg は、
 * 相対JEPX渡しとは別の独立カテゴリで返る (spec `supply-demand-gap.md` AC-SDG-004b)。
 */
export const BASELOAD_MARKET_SUPPLY_CATEGORY_CODE = 'BASELOAD_MARKET'
export const FORWARD_MARKET_SUPPLY_CATEGORY_CODE = 'FORWARD_MARKET'

/**
 * 集約表示 (供給力合計 1 本) の recharts dataKey。 `GapMainChartPoint` の予約キー。
 * `GapSupplySeries.dataKey` の判別子でもあるので (これ以外は必ず内訳カテゴリ)、
 * tooltip 側の内訳行の絞り込みもこの値との比較で行う。
 */
export const SUPPLY_TOTAL_DATA_KEY = 'supplyTotal'

/**
 * backend が返しうる供給カテゴリの表示順 (積上げは下から / 凡例は左から) と
 * 表示名の i18n キー。 **並びとラベルはこの 1 つの配列だけが決める** (別々の表に
 * 持つと片方だけに登録した区分が末尾に並ぶ / backend 名のまま出る、 をどちらも
 * 検出できないため)。 ここに無いコードは末尾へ初出順で並べ、 表示名は backend の
 * `category_name` へ落とす。
 *
 * 並び: 相対 (受渡区分 enum `TradingDeliveryType.Code()` のうち物理受渡の 3 種) →
 * ベースロード市場 → 先渡し市場 → 常時BU → 市場。 spec `supply-demand-gap.md`
 * AC-SDG-004b の固定順: 下から順に `BG` → (`ECLEAR`) → `JEPX` → `ベースロード市場` →
 * `先渡し市場` → `常時BU` → `SPOT` → `INTRADAY` → その他 (番兵)。 `ECLEAR` は spec に
 * 無い legacy の受渡区分で、 既存の位置 (BG と JEPX の間) を保つ。
 *
 * 表示名: backend の `category_name` は受渡区分名 (`TradingDeliveryType.Name()` = "BG" /
 * "eClear" / "JEPX") と市場名なので、 素通しすると凡例に「BG」「JEPX」と出る。
 * spec `supply-demand-gap.md`・design `supply/supply-gap.html` の `stackSeries`・
 * mock-app の i18n はいずれも「相対BG渡し」「相対JEPX渡し」 を正名にしており、
 * 同じ画面のガイド文言 (`page:supplyGap.chartFormula`) とも食い違う。 とくに
 * 「JEPX」 は隣に並ぶ「スポット市場」 (= JEPX 市場約定) と区別できず、 相対JEPX渡し
 * 契約を市場約定と読み違える。 en 表示も要る (backend は日本語名しか返さないので、
 * 素通しすると en locale に日本語が混ざる) ため、 市場カテゴリも含めて既知コードは
 * 全部ここを引く。
 *
 * 番兵 (受渡区分不明) は spec のカテゴリではないので**この配列に入れず**、
 * `collectSupplyCategories` が未知コードより後ろの最後尾へ回す (ラベルは
 * `UNKNOWN_SUPPLY_CATEGORY_LABEL_KEY`)。
 */
const KNOWN_SUPPLY_CATEGORIES: ReadonlyArray<{
  code: string
  labelKey: string
}> = [
  { code: 'BG', labelKey: 'page:supplyGap.legend.category.bg' },
  { code: 'ECLEAR', labelKey: 'page:supplyGap.legend.category.eclear' },
  { code: 'JEPX', labelKey: 'page:supplyGap.legend.category.jepx' },
  {
    code: BASELOAD_MARKET_SUPPLY_CATEGORY_CODE,
    labelKey: 'page:supplyGap.legend.category.baseloadMarket',
  },
  {
    code: FORWARD_MARKET_SUPPLY_CATEGORY_CODE,
    labelKey: 'page:supplyGap.legend.category.forwardMarket',
  },
  {
    code: BACKUP_REGULAR_SUPPLY_CATEGORY_CODE,
    labelKey: 'page:supplyGap.legend.category.backupRegular',
  },
  { code: 'SPOT', labelKey: 'page:supplyGap.legend.category.spot' },
  { code: 'INTRADAY', labelKey: 'page:supplyGap.legend.category.intraday' },
]

/**
 * 番兵カテゴリの表示名。 非表示にすると積上げの総和が供給力合計に届かず spec の
 * 「集約・内訳 いずれの表示でも合計値は同一」 に反するため、 必ず 1 系列として残す。
 */
const UNKNOWN_SUPPLY_CATEGORY_LABEL_KEY = 'page:supplyGap.legend.category.other'

const isKnownSupplyCategory = (code: string): boolean =>
  KNOWN_SUPPLY_CATEGORIES.some((c) => c.code === code)

/**
 * `collectSupplyCategories` の入力。 `GapAreaSeriesRow[]` を渡せる最小の構造だけを
 * 要求する (= `models/types.ts` への依存を持たず、循環 import を作らない)。
 */
type SupplyCategorySource = {
  points: ReadonlyArray<{ supply: readonly GapTradeComponent[] }>
}

/**
 * 表示中の全エリア・全コマから供給内訳のカテゴリを重複なく集める。
 * 凡例と積上げ系列でカテゴリ集合・並びを揃えるため、 エリアごとではなく
 * 表示中の response 全体から 1 回だけ集める (エリアごとに集めると、 あるエリアに
 * 無い区分の分だけ色と並びがずれて AC-SDG-003 の縦積み比較が壊れる)。
 *
 * 並びは 既知区分 (KNOWN_SUPPLY_CATEGORIES 順) → 未知コード (初出順) → 番兵。
 * 番兵は spec のカテゴリではないので、 未知コードが来ても必ず最後尾に残す。
 */
export function collectSupplyCategories(
  rows: readonly SupplyCategorySource[],
): CategoryInfo[] {
  const byCode = new Map<string, CategoryInfo>()
  for (const row of rows) {
    for (const pt of row.points) {
      for (const c of pt.supply) {
        if (!byCode.has(c.categoryCode)) {
          byCode.set(c.categoryCode, {
            code: c.categoryCode,
            name: c.categoryName,
          })
        }
      }
    }
  }

  const known = KNOWN_SUPPLY_CATEGORIES.filter(({ code }) =>
    byCode.has(code),
  ).map(({ code }) => byCode.get(code) as CategoryInfo)
  const unknown = [...byCode.values()].filter(
    (c) =>
      !isKnownSupplyCategory(c.code) && c.code !== UNKNOWN_SUPPLY_CATEGORY_CODE,
  )
  const sentinel = byCode.get(UNKNOWN_SUPPLY_CATEGORY_CODE)
  return sentinel === undefined
    ? [...known, ...unknown]
    : [...known, ...unknown, sentinel]
}

/**
 * カテゴリの系列色。 既知の区分コードは固定マップ、 **未知のコードは種類を問わず
 * 単一の fallback 色**を返す (= 色はコードだけで決まり、 response の並びや参照履歴に
 * 依存しない)。
 *
 * 未知コードどうしは同色になる = 凡例で区別できない。 producer が出すコードは
 * 9 種に閉じていて固定マップに全部載っており、 この経路に到達する producer が
 * 無いため、 一意色の割当 (ハッシュ循環 palette) は持たない (#7837)。
 */
export function getCategoryColor(code: string): string {
  return GAP_CATEGORY_COLOR_MAP[code] ?? GAP_FALLBACK_COLOR
}

/** 凡例・tooltip に出すカテゴリの表示名。 **表示名は `KNOWN_SUPPLY_CATEGORIES` と番兵キーだけが決める。** */
export function supplyCategoryLabel(cat: CategoryInfo, t: TFunction): string {
  if (cat.code === UNKNOWN_SUPPLY_CATEGORY_CODE) {
    return t(UNKNOWN_SUPPLY_CATEGORY_LABEL_KEY)
  }
  const labelKey = KNOWN_SUPPLY_CATEGORIES.find(
    (c) => c.code === cat.code,
  )?.labelKey
  return labelKey === undefined ? cat.name : t(labelKey)
}

/**
 * カテゴリ別 MW を `GapMainChartPoint` へ載せる時のキー。
 *
 * `cat:` を前置して **予約キー** (`timeLabel` / `supplyTotal` / `demandForecast` /
 * `demandPlusSales` / `position`) と名前空間を分ける。 backend の `category_code` は
 * 外から来る文字列なので、 平坦なまま同居させると予約キーと衝突した時に数値を
 * 黙って上書きする。 recharts の `dataKey` は文字列 (lodash `get` の 1 セグメント)
 * なので `cat:BG` のようなキーもそのまま解決できる。
 */
export function supplyCategoryDataKey(code: string): `cat:${string}` {
  return `cat:${code}`
}

/**
 * 供給 Bar 1 本ぶんの系列記述子。 **凡例・Bar・tooltip はこの配列だけを読む。**
 *
 * 以前は凡例側 (`index.tsx`) と Bar 側 (`GapChart.tsx`) に同じ
 * 「集約なら 1 本 / 内訳ならカテゴリ分」 の三項が別々に書かれ、 両方が色とラベルを
 * 独立に解決していた。 順序・色・ラベルの 3 属性が一致することを型もテストも
 * 保証していないので、 片方だけ触ると凡例が bar を偽って説明する。
 */
export type GapSupplySeries = {
  /**
   * recharts `dataKey` (集約 = `supplyTotal` / 内訳 = `cat:<category_code>`)。
   * 「内訳カテゴリの系列か」 はこの判別子だけで決まる (集約は必ず
   * `supplyTotal` 1 本、 内訳は必ず `cat:*`)。 tooltip の内訳行
   * (design `_sgGapTooltip` の `if (!state.simplified)` 節) はここを見て絞る:
   * 独立 field に持つと 2 つの真実源が食い違い、 「配列長 1 かどうか」 で代用すると
   * 内訳表示でカテゴリが 1 つしかないエリアの内訳行が消える。
   */
  dataKey: typeof SUPPLY_TOTAL_DATA_KEY | `cat:${string}`
  /** Bar の塗り = 凡例スウォッチ = tooltip のスウォッチ色。 */
  color: string
  /** 凡例・tooltip の表示名。 */
  label: string
}

/**
 * 表示モードと表示中カテゴリから供給系列を 1 回だけ組み立てる。
 * 'aggregate' は供給力合計 1 本、 'detail' は `categories` の並びそのまま。
 */
export function buildSupplySeries(
  breakdownMode: BreakdownMode,
  categories: readonly CategoryInfo[],
  t: TFunction,
): GapSupplySeries[] {
  if (breakdownMode === 'aggregate') {
    return [
      {
        dataKey: SUPPLY_TOTAL_DATA_KEY,
        color: GAP_CATEGORY_COLOR_MAP.SUPPLY_TOTAL,
        label: t('page:supplyGap.legend.supplyTotal'),
      },
    ]
  }
  return categories.map((cat) => ({
    dataKey: supplyCategoryDataKey(cat.code),
    color: getCategoryColor(cat.code),
    label: supplyCategoryLabel(cat, t),
  }))
}
